import { type FC, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import { getAnimationDuration } from './helpers';

import styles from './Ticker.css';

export type TickerTheme = 'dark' | 'light' | 'dark-background';

export type TTickerProps = {
  /** Текст бегущей строки */
  text: string;
  /** Тема оформления */
  theme: TickerTheme;
  /** Убрать padding слева */
  withoutPadding?: boolean;
  /** Запускать анимацию только при наведении курсора (по умолчанию строка стоит на месте) */
  playOnHover?: boolean;
};

/** Скорость прокрутки при наведении, px/s */
const HOVER_SPEED = 100;

/** Бегущая строка */
export const Ticker: FC<TTickerProps> = ({ text, theme, withoutPadding, playOnHover }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tickerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [animationDuration, setAnimationDuration] = useState(0);
  const [isInfinite, setInfinite] = useState<boolean>(false);
  const [isStatic, setStatic] = useState<boolean>(false);

  useEffect(() => {
    if (playOnHover) {
      return;
    }

    const time = getAnimationDuration(tickerRef.current, wrapperRef.current);

    if (!time) {
      setStatic(true);
    } else {
      setStatic(false);
      setAnimationDuration(time);
    }
  }, [text, isInfinite, playOnHover]);

  const hanldeAnimationEnd = (): void => setInfinite(true);

  const handleMouseOver = playOnHover
    ? (): void => {
        const wrapper = wrapperRef.current;
        const textNode = textRef.current;

        if (!wrapper || !textNode) {
          return;
        }

        const diff = textNode.offsetWidth - wrapper.offsetWidth;

        if (diff > 0) {
          textNode.style.transition = `transform ${diff / HOVER_SPEED}s linear`;
          textNode.style.transform = `translate3d(-${diff}px, 0, 0)`;
        }
      }
    : undefined;

  const handleMouseLeave = playOnHover
    ? (): void => {
        const textNode = textRef.current;

        if (textNode) {
          // Возврат строки в начальную позицию
          textNode.style.transform = '';
          textNode.style.transition = '';
        }
      }
    : undefined;

  return (
    <div
      className={clsx(
        styles['container'],
        !playOnHover && isStatic && styles['container--static'],
        !playOnHover && isInfinite && styles['container--infinite'],
        styles[`container--${theme}`],
        withoutPadding && styles['container--without-padding'],
        playOnHover && styles['container--play-hover'],
      )}
      data-testid="ticker-container"
      ref={wrapperRef}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
    >
      <div
        className={styles['ticker']}
        data-testid="ticker"
        ref={tickerRef}
        style={
          playOnHover
            ? undefined
            : { WebkitAnimationDuration: `${animationDuration}s`, animationDuration: `${animationDuration}s` }
        }
        onAnimationEnd={playOnHover ? undefined : hanldeAnimationEnd}
      >
        <div className={styles['text']} data-testid="ticker-text" ref={textRef}>
          {text}
        </div>
      </div>
    </div>
  );
};
